import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AddRoundedIcon from "@mui/icons-material/Add";
import RemoveRoundedIcon from "@mui/icons-material/Remove";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  removeCartItem,
  updateCartItem,
  getCart,
} from "../../../Redux/Customers/Cart/Action";
import { useCartDrawer } from "./CartDrawerContext";
import { useAuthModal } from "../Auth/AuthModalContext";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Slide-out mini-cart, opened after "Add to Cart" so the shopper can keep
// browsing (and see cross-sell) instead of being pushed to the cart page.
export default function CartDrawer() {
  const { isOpen, closeDrawer } = useCartDrawer();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { cart } = useSelector((store) => store);
  const items = (cart?.cartItems || []).filter(Boolean);
  const summary = cart?.cart || {};

  const go = (path) => {
    closeDrawer();
    navigate(path);
  };

  // Same rule as the cart page: guests build a cart freely, but placing an
  // order needs an account, so open sign-in rather than a checkout that 401s.
  const goToCheckout = () => {
    closeDrawer();
    if (!localStorage.getItem("jwt")) {
      openAuthModal();
      return;
    }
    navigate("/checkout?step=2");
  };

  // Available stock for an item (per-size when sized, else the flat quantity).
  const stockFor = (item) =>
    item?.product?.isSizesAvailable
      ? item.product.sizes?.find((s) => s.name === item.size)?.quantity ?? 0
      : item?.product?.quantity ?? 0;

  const changeQty = async (item, delta) => {
    const next = (item.quantity || 1) + delta;
    if (next < 1) return; // use Remove to clear the last one
    if (delta > 0 && next > stockFor(item)) return; // don't exceed stock
    await dispatch(updateCartItem({ data: { quantity: next }, cartItemId: item._id, jwt }));
    await dispatch(getCart(jwt));
  };

  const removeItem = async (item) => {
    await dispatch(removeCartItem({ cartItemId: item._id, jwt }));
    await dispatch(getCart(jwt));
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeDrawer}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in-out duration-300" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300" enterFrom="translate-x-full" enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300" leaveFrom="translate-x-0" leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-[#fffbf2] shadow-xl">
                    {/* header */}
                    <div className="flex items-center justify-between border-b border-[#eadcc0] px-5 py-4">
                      <Dialog.Title className="flex items-center gap-2 font-display text-xl font-bold text-[#511d00]">
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 22, color: "#b28585" }} />
                        Your Cart
                        {items.length > 0 && (
                          <span className="text-sm font-semibold text-[#8a7575]">
                            ({items.length})
                          </span>
                        )}
                      </Dialog.Title>
                      <button
                        type="button"
                        onClick={closeDrawer}
                        className="rounded-full p-1.5 text-[#5e3e3e] hover:bg-[#f0e6d6]"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {/* items */}
                    {items.length === 0 ? (
                      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 46, color: "#d8c3c3" }} />
                        <p className="mt-3 font-semibold text-[#511d00]">Your cart is empty</p>
                        <p className="mt-1 text-sm text-[#8a7575]">
                          Add something you love to get started.
                        </p>
                        <Button
                          onClick={() => go("/")}
                          variant="outlined"
                          sx={{ mt: 3, borderRadius: "999px", borderColor: "rgba(94,62,62,0.3)", color: "#5e3e3e", textTransform: "none" }}
                        >
                          Start Shopping
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                          <ul className="divide-y divide-[#eadcc0]">
                            {items.map((item) => {
                              const p = item?.product || {};
                              const img = Array.isArray(p.images) ? p.images[0] : p.images;
                              const atMax = item.quantity >= stockFor(item);
                              return (
                                <li key={item._id} className="flex gap-3 py-4">
                                  <button
                                    type="button"
                                    onClick={() => go(`/product/${p._id}/${p?.thirdLavelCategory?.name || ""}`)}
                                    className="h-20 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-[#eadcc0] bg-[#f3ece0]"
                                  >
                                    {img && <img src={img} alt="" className="h-full w-full object-cover" />}
                                  </button>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-[#511d00]">
                                      {p.title}
                                    </p>
                                    <p className="mt-0.5 text-xs text-[#8a7575]">
                                      {item?.size ? `Size: ${item.size}` : "Free Size"}
                                      {p.color ? ` · ${p.color}` : ""}
                                    </p>
                                    <div className="mt-2 flex items-center gap-3">
                                      {/* quantity stepper */}
                                      <div className="flex items-center rounded-full border border-[#eadcc0]">
                                        <button
                                          type="button"
                                          onClick={() => changeQty(item, -1)}
                                          disabled={item.quantity <= 1}
                                          className="flex h-7 w-7 items-center justify-center rounded-full text-[#5e3e3e] disabled:text-[#d8c3c3]"
                                          aria-label="Decrease quantity"
                                        >
                                          <RemoveRoundedIcon sx={{ fontSize: 15 }} />
                                        </button>
                                        <span className="w-6 text-center text-sm font-semibold text-[#511d00]">
                                          {item.quantity}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => changeQty(item, 1)}
                                          disabled={atMax}
                                          className="flex h-7 w-7 items-center justify-center rounded-full text-[#5e3e3e] disabled:text-[#d8c3c3]"
                                          aria-label="Increase quantity"
                                        >
                                          <AddRoundedIcon sx={{ fontSize: 15 }} />
                                        </button>
                                      </div>
                                      {/* remove */}
                                      <button
                                        type="button"
                                        onClick={() => removeItem(item)}
                                        className="flex items-center gap-0.5 text-xs font-medium text-[#b85c5c] hover:underline"
                                      >
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} /> Remove
                                      </button>
                                    </div>
                                  </div>
                                  <div className="text-right text-sm font-semibold text-[#511d00]">
                                    {inr((p.discountedPrice || 0) * (item.quantity || 1))}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>

                        {/* footer */}
                        <div className="border-t border-[#eadcc0] px-5 py-4">
                          <div className="mb-3 flex items-center justify-between">
                            <span className="text-sm font-semibold text-[#5e3e3e]">Subtotal</span>
                            <span className="text-lg font-bold text-[#511d00]">
                              {inr(summary?.totalDiscountedPrice)}
                            </span>
                          </div>
                          <p className="mb-3 text-xs text-[#8a7575]">
                            Shipping &amp; taxes calculated at checkout.
                          </p>
                          <Button
                            onClick={goToCheckout}
                            variant="contained"
                            fullWidth
                            sx={{ borderRadius: "999px", py: 1.2, fontWeight: 700, textTransform: "none" }}
                          >
                            Checkout
                          </Button>
                          <div className="mt-2 flex gap-2">
                            <Button
                              onClick={() => go("/cart")}
                              variant="outlined"
                              fullWidth
                              sx={{ borderRadius: "999px", borderColor: "rgba(94,62,62,0.3)", color: "#5e3e3e", textTransform: "none" }}
                            >
                              View Cart
                            </Button>
                            <Button
                              onClick={closeDrawer}
                              fullWidth
                              sx={{ borderRadius: "999px", color: "#5e3e3e", textTransform: "none" }}
                            >
                              Continue Shopping
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
